import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./KYCManagementPage.css";

const STATUS_TABS = [
  { key: "all", label: "All", apiStatus: "all" },
  { key: "pending", label: "Pending", apiStatus: "pending" },
  { key: "approved", label: "Approved", apiStatus: "approved" },
  { key: "rejected", label: "Rejected", apiStatus: "rejected" },
];

const formatDate = (iso) => {
  if (iso == null || iso === "") return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
};

const KYCManagementPage = () => {
  const navigate = useNavigate();
  const [kycList, setKycList] = useState([]);
  const [counts, setCounts] = useState({ all: 0, pending: 0, approved: 0, rejected: 0 });
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });

  const apiStatus = STATUS_TABS.find((t) => t.key === activeTab)?.apiStatus ?? "all";

  const fetchCounts = useCallback(async () => {
    const reqs = STATUS_TABS.map((t) => AdminService.getKycList({ status: t.apiStatus, page: 1, limit: 1 }));
    const results = await Promise.all(reqs);
    const next = {};
    STATUS_TABS.forEach((t, i) => {
      next[t.key] = results[i]?.pagination?.total ?? 0;
    });
    setCounts(next);
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError("");
    const params = {
      status: apiStatus,
      page: pagination.page,
      limit: pagination.limit,
      ...(searchDebounced.trim() && { search: searchDebounced.trim() }),
    };
    const res = await AdminService.getKycList(params);
    setLoading(false);
    if (res?.success) {
      setKycList(Array.isArray(res.data) ? res.data : []);
      const pag = res.pagination || {};
      setPagination((prev) => ({
        ...prev,
        total: pag.total ?? prev.total,
        totalPages: pag.total_pages ?? pag.totalPages ?? prev.totalPages,
      }));
      if (pag.total != null) setCounts((c) => ({ ...c, [activeTab]: pag.total }));
    } else {
      setError(res?.message || "Failed to load KYC list.");
      setKycList([]);
    }
  }, [apiStatus, pagination.page, pagination.limit, searchDebounced, activeTab]);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPagination((p) => ({ ...p, page: 1 }));
  }, [apiStatus, searchDebounced]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    setPagination((p) => ({ ...p, page: newPage }));
  };

  const handleView = (row) => {
    const userId = row.user_id ?? row._id;
    if (userId) navigate(`/dashboard/trader/${userId}?tab=kyc`);
  };

  const getDocTypes = (row) => {
    const parts = [];
    if (row.id_document?.type) parts.push(row.id_document.type);
    if (row.tax_document?.type) parts.push(row.tax_document.type);
    if (row.selfie && (row.selfie.status || row.id_document)) parts.push("Selfie");
    return parts.length ? parts.join(", ") : "—";
  };

  const getStatusBadgeClass = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "approved" || s === "verified") return "badge--kyc-approved";
    if (s === "rejected") return "badge--kyc-rejected";
    return "badge--kyc-pending"; // pending, submitted, etc.
  };

  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>KYC Management</span>
          </p>
          <h1 className="dashboard-hero__title">KYC Management</h1>
          <p className="dashboard-hero__subtitle">
            Review KYC submissions. Click View to open trader details and approve/reject.
          </p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-id-card" />
        </div>
      </section>

      <div className="dashboard-body">
        <section className="dashboard-activity-section">
          <div className="dashboard-activity-header">
            <div className="dashboard-activity-header__icon">
              <i className="fas fa-redo" />
            </div>
            <h2 className="dashboard-activity-header__title">KYC Management</h2>
          </div>

          <div className="kyc-filters">
            <input
              type="text"
              placeholder="Search email, UUID, mobile, name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="kyc-search"
            />
          </div>

          <div className="kyc-tabs">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`kyc-tab ${activeTab === tab.key ? "kyc-tab--active" : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
                <span className="kyc-tab__count">{counts[tab.key] ?? 0}</span>
              </button>
            ))}
          </div>

          {error && (
            <div className="kyc-error">
              <i className="fas fa-exclamation-circle" /> {error}
            </div>
          )}

          <div className="dashboard-content-inner kyc-content-inner">
            <div className="table-wrap kyc-table-wrap">
              <table className="data-table kyc-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Document Type</th>
                    <th>Status</th>
                    <th>Submitted</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && kycList.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="kyc-table__empty">
                        <i className="fas fa-spinner fa-spin" /> Loading…
                      </td>
                    </tr>
                  ) : kycList.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="kyc-table__empty">
                        No records in this category.
                      </td>
                    </tr>
                  ) : (
                    kycList.map((row) => {
                      const user = row.user || {};
                      const userLabel = [user.email_masked, user.uuid, user.mobile_masked].filter(Boolean).join(" · ") || "—";
                      const fullName = [row.first_name, row.last_name].filter(Boolean).join(" ") || "";
                      const displayUser = fullName ? `${fullName} · ${userLabel}` : userLabel;
                      return (
                        <tr key={row._id}>
                          <td className="kyc-table__user">{displayUser}</td>
                          <td>{getDocTypes(row)}</td>
                          <td>
                            <span className={`badge ${getStatusBadgeClass(row.overall_status)}`}>
                              {row.overall_status || "—"}
                            </span>
                          </td>
                          <td>{formatDate(row.submitted_at)}</td>
                          <td className="kyc-table__actions">
                            <button
                              type="button"
                              className="btn btn-sm btn-primary kyc-view-btn"
                              onClick={() => handleView(row)}
                              title="View KYC in trader details"
                            >
                              <i className="fas fa-eye" /> View
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && !loading && (
              <div className="kyc-pagination">
                <span className="kyc-pagination__info">
                  Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
                </span>
                <div className="kyc-pagination__buttons">
                  <button
                    type="button"
                    className="kyc-pagination__btn"
                    disabled={pagination.page <= 1}
                    onClick={() => handlePageChange(pagination.page - 1)}
                  >
                    <i className="fas fa-chevron-left" /> Prev
                  </button>
                  <button
                    type="button"
                    className="kyc-pagination__btn"
                    disabled={pagination.page >= pagination.totalPages}
                    onClick={() => handlePageChange(pagination.page + 1)}
                  >
                    Next <i className="fas fa-chevron-right" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </main>
  );
};

export default KYCManagementPage;
